import type { UserResponse } from '@api/users-api';
import {
  getUserVoteEditions,
  getVotesByUser,
  type VoteUserDetailEditionResponse,
  type VoteUserDetailResponse,
} from '@api/votes-api';
import Modal from '@components/modal';
import { ChevronLeft, ChevronRight, Vote } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

const VOTES_PAGE_SIZE = 10;

interface UserVotesModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: UserResponse | null;
}

function formatVoteDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatParticipantName(name: string, surname: string | null): string {
  return [name, surname].filter(Boolean).join(' ');
}

export default function UserVotesModal({
  isOpen,
  onClose,
  user,
}: UserVotesModalProps): React.JSX.Element | null {
  const [editions, setEditions] = useState<VoteUserDetailEditionResponse[]>([]);
  const [selectedEditionId, setSelectedEditionId] = useState<string | null>(null);
  const [votes, setVotes] = useState<VoteUserDetailResponse[]>([]);
  const [isLoadingEditions, setIsLoadingEditions] = useState(false);
  const [isLoadingVotes, setIsLoadingVotes] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    if (!user) {
      setEditions([]);
      setSelectedEditionId(null);
      setVotes([]);
      setCurrentPage(1);
      setTotalItems(0);
      setTotalPages(1);
      setError(null);
      return;
    }

    setIsLoadingEditions(true);
    setError(null);

    void getUserVoteEditions(user.id)
      .then((editionList) => {
        setEditions(editionList);
        setSelectedEditionId(editionList[0]?.id ?? null);
        setCurrentPage(1);
      })
      .catch(() => {
        setEditions([]);
        setError('Failed to load editions');
      })
      .finally(() => setIsLoadingEditions(false));
  }, [user]);

  useEffect(() => {
    if (!user || !selectedEditionId) {
      setVotes([]);
      setTotalItems(0);
      setTotalPages(1);
      return;
    }

    setIsLoadingVotes(true);
    setError(null);

    void getVotesByUser(
      user.id,
      { page: currentPage, pageSize: VOTES_PAGE_SIZE },
      selectedEditionId,
    )
      .then((response) => {
        setVotes(response.data);
        setTotalItems(response.meta.totalItems);
        setTotalPages(response.meta.totalPages);
      })
      .catch(() => {
        setVotes([]);
        setError('Failed to load votes');
      })
      .finally(() => setIsLoadingVotes(false));
  }, [user, selectedEditionId, currentPage]);

  const handleEditionChange = useCallback((editionId: string): void => {
    setSelectedEditionId(editionId);
    setCurrentPage(1);
  }, []);

  const goToPage = useCallback((page: number): void => {
    setCurrentPage(page);
  }, []);

  if (!user) return null;

  const fullName = [user.name, user.surname].filter(Boolean).join(' ') || user.email;
  const startIndex = totalItems === 0 ? 0 : (currentPage - 1) * VOTES_PAGE_SIZE + 1;
  const endIndex = Math.min(currentPage * VOTES_PAGE_SIZE, totalItems);
  const isLoading = isLoadingEditions || isLoadingVotes;
  const hasNoEditions = !isLoadingEditions && editions.length === 0 && !error;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Votes by ${fullName}`} className="modal--wide">
      <div className="user-votes-modal">
        <div className="user-votes-modal__user-info">
          <div className="user-votes-modal__user-field">
            <span className="user-votes-modal__label">Email</span>
            <span className="user-votes-modal__value">{user.email}</span>
          </div>
          {user.name && (
            <div className="user-votes-modal__user-field">
              <span className="user-votes-modal__label">Name</span>
              <span className="user-votes-modal__value">{fullName}</span>
            </div>
          )}
          <div className="user-votes-modal__user-field">
            <span className="user-votes-modal__label">Votes in edition</span>
            <span className="user-votes-modal__value user-votes-modal__value--highlight">
              {isLoading ? '…' : totalItems}
            </span>
          </div>
        </div>

        {editions.length > 0 && (
          <div className="uvm-tabs">
            {editions.map((edition) => (
              <button
                key={edition.id}
                type="button"
                className={`uvm-tabs__tab${selectedEditionId === edition.id ? ' uvm-tabs__tab--active' : ''}`}
                onClick={() => handleEditionChange(edition.id)}
              >
                {edition.name}
                <span className="uvm-tabs__year">{edition.year}</span>
              </button>
            ))}
          </div>
        )}

        {isLoadingEditions && (
          <div className="user-votes-modal__empty">
            <p>Loading editions...</p>
          </div>
        )}

        {error && (
          <div className="page-error" role="alert">
            <span>{error}</span>
          </div>
        )}

        {hasNoEditions && (
          <div className="user-votes-modal__empty">
            <Vote size={32} strokeWidth={1} />
            <p>No votes found for this user.</p>
          </div>
        )}

        {isLoadingVotes && !isLoadingEditions && (
          <div className="user-votes-modal__empty">
            <p>Loading votes...</p>
          </div>
        )}

        {!isLoading && !error && votes.length === 0 && selectedEditionId && (
          <div className="user-votes-modal__empty">
            <Vote size={32} strokeWidth={1} />
            <p>No votes in this edition.</p>
          </div>
        )}

        {!isLoading && !error && votes.length > 0 && (
          <>
            <div className="user-votes-modal__table-wrap">
              <table className="data-table data-table--compact">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Voted for</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {votes.map((vote) => (
                    <tr key={vote.id}>
                      <td>{vote.category.name}</td>
                      <td>
                        {formatParticipantName(
                          vote.nomination.participant.name,
                          vote.nomination.participant.surname,
                        )}
                      </td>
                      <td className="user-votes-modal__date">{formatVoteDate(vote.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="user-votes-modal__pagination">
                <span className="pagination__info">
                  {startIndex}&ndash;{endIndex} of {totalItems}
                </span>
                <div className="pagination__controls">
                  <button
                    type="button"
                    className="pagination__btn"
                    disabled={currentPage === 1}
                    aria-label="Previous page"
                    onClick={() => goToPage(currentPage - 1)}
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <span className="pagination__info">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    type="button"
                    className="pagination__btn"
                    disabled={currentPage === totalPages}
                    aria-label="Next page"
                    onClick={() => goToPage(currentPage + 1)}
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
